import {
  ApiAttributionModel,
  ApiChartAttribute,
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
} from "@incendium/api";
import { Box } from "@mui/material";
import {
  AttributionHeader,
  metricsByAttributionModelType,
  table10Rows,
} from "features/analytics";
import StandardAnalyticsCard from "features/analytics/components/StandardAnalyticsCard";
import {
  CHART_KEY_ATTRIBUTION_HEADER_TOP_BY_TREND,
  CHART_KEY_DASHBOARD_CAMPAIGNS_TABLE,
} from "features/analytics/constants/chartKeys";
import withPaidChannelAttributes from "HoC/withPaidChannelAttributes";
import { useMemo } from "react";
import {
  dashboardChartKey,
  useIsEcommerceProject,
  withoutAddToCartMetrics,
} from "features/dashboard/utils/ecommerceMetrics";

interface ICampaignsTabProps {
  attributes: ApiChartAttribute[];
  paidChannelAttributes: ApiChartAttribute[];
}

function CampaignsTab({
  attributes,
  paidChannelAttributes,
}: ICampaignsTabProps) {
  const isEcommerce = useIsEcommerceProject();
  const fullAttributes = useMemo(
    () => [...attributes, ...paidChannelAttributes],
    [attributes, paidChannelAttributes],
  );
  const attrMetrics = useMemo(
    () => metricsByAttributionModelType(ApiAttributionModel.NO_MODEL_SELECTED),
    [],
  );
  const tableFields = useMemo(
    () =>
      withoutAddToCartMetrics(
        [
          ApiMetric.METRIC_SESSIONS_COUNT,
          ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
          ApiMetric.METRIC_AVERAGE_PAGEVIEWS_PER_SESSION,
          ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_RATE,
          ...attrMetrics,
          ApiMetric.METRIC_CPA,
          ApiMetric.METRIC_VISIT_TO_PURCHASE_RATE,
          ApiMetric.METRIC_CPNV,
        ],
        isEcommerce,
      ),
    [attrMetrics, isEcommerce],
  );

  return (
    <>
      <Box sx={{ flexShrink: 0, width: "100%", minWidth: 0 }}>
        <AttributionHeader
          chartKey={`${CHART_KEY_ATTRIBUTION_HEADER_TOP_BY_TREND}_dashboard_campaigns`}
          dimension={ApiDimension.DIMENSION_CAMPAIGN}
          title="Paid Campaigns"
          attributes={fullAttributes}
        />
      </Box>

      <Box
        sx={{
          height: { xs: 480, md: table10Rows },
          flexShrink: 0,
          width: "100%",
          minWidth: 0,
          overflowX: "auto",
        }}
      >
        <StandardAnalyticsCard
          chartKey={dashboardChartKey(
            CHART_KEY_DASHBOARD_CAMPAIGNS_TABLE,
            isEcommerce,
          )}
          staticAttributes={fullAttributes}
          chart={{
            name: "Paid Campaign Performance",
            dimension: [ApiDimension.DIMENSION_CAMPAIGN],
            template: ApiChartTemplate.TABLE,
            type: ApiChartType.TABLE,
            attributes: [],
            comparison: true,
            yAxisKeys: [
              {
                key: "l",
                fields: tableFields,
              },
            ],
            displayOptions: {
              rowsPerPage: 0,
              noFilters: true,
              showTotals: true,
            },
          }}
        />
      </Box>
    </>
  );
}

export default withPaidChannelAttributes(CampaignsTab);
